import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Spin,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import api from "../services/api";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const Matches = () => {
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isMatchModalVisible, setIsMatchModalVisible] = useState(false);
  const [matchForm] = Form.useForm();
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);

  const [isTeamModalVisible, setIsTeamModalVisible] = useState(false);
  const [teamForm] = Form.useForm();
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchTeams, setMatchTeams] = useState([]);

  const navigate = useNavigate();

  // Fetch matches
  const fetchMatches = async (page = 1, pageSize = 10) => {
    if (page <= 0) return;
    setLoading(true);
    try {
      const res = await api.get("/matches", { params: { page, pageSize } });
      const { data, total } = res.data;
      setMatches(data);
      setPagination((prev) => ({ ...prev, total, current: page }));
    } catch (err) {
      console.error(err);
      message.error("Failed to load matches");
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const res = await api.get("/tournaments");
      setTournaments(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await api.get("/teams");
      setTeams(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMatchTeams = async (matchId) => {
    try {
      const res = await api.get("/match-teams", { params: { match_id: matchId } });
      setMatchTeams(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchMatches(pagination.current, pagination.pageSize);
    fetchTournaments();
    fetchTeams();
  }, []);

  const handleDeleteMatch = async (matchId) => {
    try {
      await api.delete(`/matches/${matchId}`);
      message.success("Match deleted successfully");
      fetchMatches(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Failed to delete match");
    }
  };

  const handleEditMatch = (match) => {
    matchForm.setFieldsValue({
      match_id: match.match_id,
      name: match.name,
      tournament_id: match.tournament_id,
      start_date: moment(match.start_time),
      end_date: match.end_time ? moment(match.end_time) : null,
      level: match.match_type,
      status: match.status,
      overs: match.overs,
      youtube_link: match.youtube_link || "",
    });
    setPosterPreview(match.poster ? match.poster : null);
    setPosterFile(null);
    setIsMatchModalVisible(true);
  };

  const handleCreateOrUpdateMatch = async (values) => {
    try {
      setLoading(true);
      const payload = {
        name: values.name,
        tournament_id: values.tournament_id,
        start_time: values.start_date.format("YYYY-MM-DD HH:mm:ss"),
        end_time: values.end_date ? values.end_date.format("YYYY-MM-DD HH:mm:ss") : null,
        match_type: values.level || "domestic",
        status: values.status || "upcoming",
        overs: values.overs || 20,
        result: values.result || null,
      };

      let matchId = values.match_id;

      if (matchId) {
        await api.put(`/matches/${matchId}`, payload);
        message.success("Match updated successfully");
      } else {
        const res = await api.post("/matches", payload);
        matchId = res.data.data.match_id;
        message.success("Match created successfully");
      }

      // YouTube link
      if (values.youtube_link) {
        await api.put(`/matches/${matchId}/youtube-link`, {
          youtube_link: values.youtube_link,
        });
      }

      // Poster upload
      if (posterFile) {
        const formData = new FormData();
        formData.append("poster", posterFile);
        await api.post(`/matches/${matchId}/upload-poster`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setIsMatchModalVisible(false);
      matchForm.resetFields();
      setPosterFile(null);
      setPosterPreview(null);
      fetchMatches(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Failed to save match");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeamModal = (match) => {
    setSelectedMatch(match);
    fetchMatchTeams(match.match_id);
    teamForm.resetFields();
    setIsTeamModalVisible(true);
  };

  const handleAddTeam = async (values) => {
    try {
      const payload = {
        match_id: selectedMatch.match_id,
        team_id: values.team_id,
        role: values.role,
      };
      await api.post("/match-teams", payload);
      message.success("Team added successfully");
      teamForm.resetFields();
      fetchMatchTeams(selectedMatch.match_id);
    } catch (err) {
      console.error(err);
      message.error("Failed to add team");
    }
  };

  // RESPONSIVE TABLE COLUMNS
  const matchColumns = [
    {
      title: "Match Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      width: 180,
    },
    {
      title: "Tournament",
      dataIndex: "tournament_id",
      key: "tournament_id",
      width: 150,
      render: (id) => tournaments.find((t) => t.tournament_id === id)?.name || "N/A",
    },
    {
      title: "Start Date",
      dataIndex: "start_time",
      key: "start_time",
      width: 180,
      render: (date) => moment(date).format("DD MMM YYYY, HH:mm"),
    },
    {
      title: "Poster",
      dataIndex: "poster",
      key: "poster",
      width: 100,
      render: (url) =>
        url ? (
          <img
            src={url}
            alt="poster"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "1px solid #ddd",
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "YouTube",
      dataIndex: "youtube_link",
      key: "youtube_link",
      width: 120,
      ellipsis: true,
      render: (link) =>
        link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            View Live
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button type="link" onClick={() => navigate(`/dashboard/matches/${record.match_id}`)}>
            Details
          </Button>
          <Button type="link" onClick={() => handleEditMatch(record)}>
            Edit
          </Button>
          <Button type="link" onClick={() => handleAddTeamModal(record)}>
            Add Team
          </Button>
          <Popconfirm
            title="Are you sure to delete this match?"
            onConfirm={() => handleDeleteMatch(record.match_id)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const matchTeamsColumns = [
    { title: "Team", dataIndex: "team_name", key: "team_name" },
    { title: "Role", dataIndex: "role", key: "role" },
  ];

  return (
    <div>
      <style>
        {`
        @media (max-width: 600px) {
          .mobile-modal .ant-modal {
            width: 95% !important;
            max-width: none !important;
            margin: 0 auto;
          }
        }
      `}
      </style>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Matches Management</h2>
        <Button
          type="primary"
          onClick={() => {
            matchForm.resetFields();
            setPosterFile(null);
            setPosterPreview(null);
            setIsMatchModalVisible(true);
          }}
        >
          Create Match
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={matchColumns}
          dataSource={matches}
          rowKey="match_id"
          pagination={pagination}
          scroll={{ x: 1200 }} // mobile responsive horizontal scroll
          size="small"
          onChange={(p) => fetchMatches(p.current, p.pageSize)}
        />
      )}

      {/* Match Modal */}
      <Modal
        title="Create / Edit Match"
        open={isMatchModalVisible}
        onCancel={() => setIsMatchModalVisible(false)}
        onOk={() => matchForm.submit()}
        okText="Save"
        confirmLoading={loading}
        className="mobile-modal"
      >
        <Form form={matchForm} layout="vertical" onFinish={handleCreateOrUpdateMatch}>
          <Form.Item name="match_id" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item name="name" label="Match Name" rules={[{ required: true }]}>
            <Input placeholder="Enter match name" />
          </Form.Item>

          <Form.Item name="tournament_id" label="Tournament" rules={[{ required: true }]}>
            <Select placeholder="Select tournament">
              {tournaments.map((t) => (
                <Option key={t.tournament_id} value={t.tournament_id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="end_date" label="End Date">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="level" label="Level">
            <Select placeholder="Select level">
              <Option value="international">International</Option>
              <Option value="domestic">Domestic</Option>
              <Option value="local">Local</Option>
            </Select>
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select placeholder="Select status">
              <Option value="upcoming">Upcoming</Option>
              <Option value="live">Live</Option>
              <Option value="completed">Completed</Option>
            </Select>
          </Form.Item>

          <Form.Item name="overs" label="Overs">
            <Input type="number" placeholder="Enter overs" />
          </Form.Item>

          <Form.Item name="youtube_link" label="YouTube Live Link">
            <Input placeholder="https://youtube.com/live/..." />
          </Form.Item>

          <Form.Item label="Poster Image">
            <Upload
              beforeUpload={(file) => {
                setPosterFile(file);
                setPosterPreview(URL.createObjectURL(file));
                return false;
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Select Poster</Button>
            </Upload>

            {posterPreview && (
              <img
                src={posterPreview}
                alt="poster preview"
                style={{ width: "100%", marginTop: 10, borderRadius: 4 }}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Team Modal */}
      <Modal
        title={`Add Team to Match: ${selectedMatch?.name}`}
        open={isTeamModalVisible}
        onCancel={() => setIsTeamModalVisible(false)}
        onOk={() => teamForm.submit()}
        okText="Add Team"
        confirmLoading={loading}
        className="mobile-modal"
      >
        <Form form={teamForm} layout="inline" onFinish={handleAddTeam}>
          <Form.Item name="team_id" rules={[{ required: true }]}>
            <Select placeholder="Select Team" style={{ width: 200 }}>
              {teams.map((t) => (
                <Option key={t.team_id} value={t.team_id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="role" rules={[{ required: true }]}>
            <Select placeholder="Select Role" style={{ width: 150 }}>
              <Option value="home">Home</Option>
              <Option value="away">Away</Option>
              <Option value="team1">Team1</Option>
              <Option value="team2">Team2</Option>
            </Select>
          </Form.Item>
        </Form>

        <h4 style={{ marginTop: 20 }}>Teams in this Match</h4>
        <Table columns={matchTeamsColumns} dataSource={matchTeams} rowKey="id" pagination={false} />
      </Modal>
    </div>
  );
};

export default Matches;
